/**
 * InlineSignaturePad — canvas-based signature capture that renders inline in the chat.
 *
 * Renders a drawing surface (or an accessible type-to-sign fallback) when the AI
 * requests a signature. On "Done", converts the canvas to a base64 PNG and calls
 * onSign. On "Clear", resets the canvas.
 *
 * Coordinate / DPR correctness:
 *   The backing store is sized to the rendered box * devicePixelRatio and the
 *   context is scaled by dpr. With that invariant in place, pointer math is just
 *   `clientX - rect.left` (CSS pixels) which maps 1:1 into the dpr-scaled context.
 *   A ResizeObserver re-runs setup on mount, rotation, and responsive reflow,
 *   preserving any existing strokes via a toDataURL snapshot.
 *
 * Uses a plain HTML canvas — no external dependency.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';

interface Props {
  onSign: (base64Png: string) => void;
  onCancel?: () => void;
}

type SignMode = 'draw' | 'type';

// Dark ink for the signature line. var() is not usable inside canvas ctx, so we
// resolve --ci-text at draw time and fall back to the portal navy literal.
const STROKE_FALLBACK = '#1a2744';
const SCRIPT_FONT = '"Brush Script MT", "Segoe Script", cursive';

function CheckIcon(): React.ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function InlineSignaturePad({ onSign, onCancel }: Props): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef   = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn,  setHasDrawn]  = useState(false);
  const [signMode,  setSignMode]  = useState<SignMode>('draw');
  const [typedName, setTypedName] = useState('');
  const [liveMsg,   setLiveMsg]   = useState('');
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  const isEmpty = signMode === 'draw' ? !hasDrawn : typedName.trim().length < 2;

  // Resolve the stroke colour from the live CSS variable (teal portal theme).
  const strokeColor = useCallback((canvas: HTMLCanvasElement): string => {
    const v = getComputedStyle(canvas).getPropertyValue('--ci-text').trim();
    return v || STROKE_FALLBACK;
  }, []);

  // ---------------------------------------------------------------------------
  // DPR-aware canvas setup (runs on mount + resize)
  // ---------------------------------------------------------------------------

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const snapshot = hasDrawn ? canvas.toDataURL() : null;

    canvas.width  = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = strokeColor(canvas);
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';

    if (snapshot) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = snapshot;
    }
  }, [hasDrawn, strokeColor]);

  useEffect(() => {
    resizeCanvas();
    const ro = new ResizeObserver(() => resizeCanvas());
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [resizeCanvas]);

  // ---------------------------------------------------------------------------
  // Drawing helpers
  // ---------------------------------------------------------------------------

  // Invariant: backing store = rect * dpr, ctx scaled by dpr, so client-rect
  // offsets are already CSS pixels and map 1:1 into the scaled context.
  function getPos(e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return null;
    if ('touches' in e) {
      const touch = e.touches[0];
      if (!touch) return null;
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    return {
      x: (e as React.MouseEvent).clientX - rect.left,
      y: (e as React.MouseEvent).clientY - rect.top,
    };
  }

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
    setIsDrawing(true);
    setHasDrawn(true);
  }, []);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const pos = getPos(e);
    if (!pos) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, [isDrawing]);

  const endDraw = useCallback(() => {
    setIsDrawing(false);
    lastPos.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
    setHasDrawn(false);
    setTypedName('');
    setLiveMsg('Signature cleared');
  }, []);

  const handleDone = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (signMode === 'type') {
      const name = typedName.trim();
      if (name.length < 2) return;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.fillStyle = strokeColor(canvas);
      ctx.font = `32px ${SCRIPT_FONT}`;
      ctx.textBaseline = 'middle';
      ctx.fillText(name, 16, rect.height / 2);
    }

    const base64 = canvas.toDataURL('image/png');
    setLiveMsg('Signature captured');
    onSign(base64);
  }, [onSign, signMode, typedName, strokeColor]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="ci-portal ci-enter-fast" style={styles.container} role="group" aria-label="Signature">
      <p style={styles.instruction}>Please sign below:</p>

      {/* Draw / Type segmented toggle */}
      <div style={styles.toggle}>
        <button
          type="button"
          style={{ ...styles.toggleBtn, ...(signMode === 'draw' ? styles.toggleBtnActive : {}) }}
          aria-pressed={signMode === 'draw'}
          onClick={() => setSignMode('draw')}
        >
          Draw
        </button>
        <button
          type="button"
          style={{ ...styles.toggleBtn, ...(signMode === 'type' ? styles.toggleBtnActive : {}) }}
          aria-pressed={signMode === 'type'}
          onClick={() => setSignMode('type')}
        >
          Type
        </button>
      </div>

      {signMode === 'draw' ? (
        <div ref={wrapRef} style={styles.canvasWrap}>
          <canvas
            ref={canvasRef}
            style={styles.canvas}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {!hasDrawn && (
            <div style={styles.placeholder} aria-hidden="true">
              Draw your signature here
            </div>
          )}
        </div>
      ) : (
        <div ref={wrapRef} style={styles.typeWrap}>
          <input
            className="ci-focusable"
            style={styles.typeInput}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="Type your full legal name"
            autoComplete="name"
            aria-label="Type your full name to sign"
          />
          <span style={styles.scriptPreview}>{typedName || ' '}</span>
          {/* Hidden canvas keeps the onSign(PNG) contract identical in type mode */}
          <canvas ref={canvasRef} style={styles.hiddenCanvas} aria-hidden="true" />
        </div>
      )}

      <div style={styles.actions}>
        <button style={styles.clearBtn} onClick={handleClear} disabled={isEmpty}>
          Clear
        </button>
        {onCancel && (
          <button style={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          style={{ ...styles.doneBtn, opacity: isEmpty ? 0.5 : 1 }}
          onClick={handleDone}
          disabled={isEmpty}
          aria-disabled={isEmpty}
        >
          Done
          <CheckIcon />
        </button>
      </div>

      <span style={styles.srOnly} aria-live="polite">{liveMsg}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background:   '#ffffff',
    border:       '1px solid var(--ci-border)',
    borderRadius: 'var(--ci-radius-lg)',
    padding:      '12px 14px',
    margin:       '4px 0 8px',
    fontFamily:   'var(--ci-font)',
  },
  instruction: {
    margin:     '0 0 8px',
    fontSize:   13,
    color:      'var(--ci-text-body)',
    fontWeight: 500,
    fontFamily: 'var(--ci-font)',
  },
  toggle: {
    display:      'flex',
    gap:          4,
    marginBottom: 10,
  },
  toggleBtn: {
    flex:         1,
    minHeight:    40,
    border:       '1px solid var(--ci-border)',
    borderRadius: 'var(--ci-radius)',
    background:   '#fff',
    color:        'var(--ci-text-body)',
    fontFamily:   'var(--ci-font)',
    fontSize:     13,
    fontWeight:   500,
    cursor:       'pointer',
  },
  toggleBtnActive: {
    borderColor: 'var(--ci-primary)',
    color:       'var(--ci-primary)',
    background:  'var(--ci-primary-light)',
  },
  canvasWrap: {
    position: 'relative',
    display:  'block',
    width:    '100%',
  },
  canvas: {
    display:      'block',
    border:       '1px solid var(--ci-border-light)',
    borderRadius: 'var(--ci-radius)',
    background:   'var(--ci-surface)',
    cursor:       'crosshair',
    width:        '100%',
    height:       150,
    touchAction:  'none',
    boxSizing:    'border-box',
  },
  placeholder: {
    position:      'absolute',
    top:           '50%',
    left:          '50%',
    transform:     'translate(-50%, -50%)',
    fontSize:      13,
    color:         'var(--ci-text-muted)',
    opacity:       0.6,
    pointerEvents: 'none',
    userSelect:    'none',
  },
  typeWrap: {
    display:       'flex',
    flexDirection: 'column',
    gap:           8,
  },
  typeInput: {
    width:        '100%',
    minHeight:    44,
    padding:      '11px 12px',
    border:       '1px solid var(--ci-border)',
    borderRadius: 'var(--ci-radius)',
    fontSize:     15,
    fontFamily:   'var(--ci-font)',
    color:        'var(--ci-text)',
    boxSizing:    'border-box',
    outline:      'none',
  },
  scriptPreview: {
    fontFamily: SCRIPT_FONT,
    fontSize:   26,
    color:      'var(--ci-text)',
    minHeight:  40,
    paddingLeft: 4,
  },
  hiddenCanvas: {
    position: 'absolute',
    width:    360,
    height:   150,
    left:     -99999,
    top:      0,
  },
  actions: {
    display:        'flex',
    justifyContent: 'flex-end',
    flexWrap:       'wrap',
    gap:            8,
    marginTop:      10,
  },
  clearBtn: {
    padding:      '11px 16px',
    minHeight:    44,
    background:   'none',
    border:       '1px solid var(--ci-border)',
    borderRadius: 'var(--ci-radius)',
    fontSize:     13,
    cursor:       'pointer',
    color:        'var(--ci-text-body)',
    fontFamily:   'var(--ci-font)',
  },
  cancelBtn: {
    padding:      '11px 16px',
    minHeight:    44,
    background:   'none',
    border:       '1px solid var(--ci-border)',
    borderRadius: 'var(--ci-radius)',
    fontSize:     13,
    cursor:       'pointer',
    color:        'var(--ci-text-muted)',
    fontFamily:   'var(--ci-font)',
  },
  doneBtn: {
    display:        'inline-flex',
    alignItems:     'center',
    gap:            6,
    padding:        '11px 18px',
    minHeight:      44,
    background:     'var(--ci-primary)',
    color:          '#fff',
    border:         'none',
    borderRadius:   'var(--ci-radius)',
    fontSize:       13,
    fontWeight:     600,
    cursor:         'pointer',
    fontFamily:     'var(--ci-font)',
  },
  srOnly: {
    position:   'absolute',
    width:      1,
    height:     1,
    overflow:   'hidden',
    clip:       'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
};
